'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Search, Trash2, Loader2 } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { toast } from 'sonner';
import { harvestService, type HarvestRecord } from '@/lib/services/farmService';
import { createClient } from '@/lib/supabase/client';

export default function HarvestHistoryTable() {
  const [harvests, setHarvests] = useState<HarvestRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [liveIndicator, setLiveIndicator] = useState(false);

  const loadHarvests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await harvestService.getAll();
      setHarvests(data);
    } catch {
      toast.error('Failed to load harvest records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHarvests();
  }, [loadHarvests]);

  // Real-time subscription
  useEffect(() => {
    const supabase = createClient();

    const channel = supabase
      .channel('rt-harvest-history')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'harvest_records' },
        async () => {
          setLiveIndicator(true);
          setTimeout(() => setLiveIndicator(false), 1500);
          const data = await harvestService.getAll();
          setHarvests(data);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = harvests.filter((h) =>
    h.bedId.toLowerCase().includes(search.toLowerCase()) ||
    (h.loggedBy ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async () => {
    if (!deleteId) return;
    const ok = await harvestService.delete(deleteId);
    if (ok) {
      setHarvests((prev) => prev.filter((h) => h.id !== deleteId));
      toast.success('Harvest record deleted');
    } else {
      toast.error('Failed to delete harvest record');
    }
    setDeleteId(null);
  };

  const gradeVariant = (g: string) =>
    g === 'A' ? 'grade-a' as const : g === 'B' ? 'grade-b' as const : 'grade-c' as const;

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm">
      <div className="px-4 py-3 border-b border-border flex items-center gap-3">
        <h2 className="text-base font-semibold text-foreground flex-1">Harvest History</h2>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full transition-colors ${liveIndicator ? 'bg-success animate-pulse' : 'bg-success/50'}`} />
          <span className="text-xs text-muted-foreground">Live</span>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-7 pr-3 py-2 text-sm rounded-lg border border-border bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring w-32"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {['Bed', 'Date', 'Wet (kg)', 'Dry (kg)', 'Volume (L)', 'Grade', 'Moisture %', 'Logged By', ''].map((h) => (
                  <th key={`hv-col-${h}`} className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {harvests.length === 0 ? 'No harvest records yet — save your first record above' : 'No harvest records match your search'}
                  </td>
                </tr>
              )}
              {filtered.map((h) => (
                <tr key={h.id} className="hover:bg-muted/40 transition-colors">
                  <td className="px-3 py-3 font-semibold text-foreground">{h.bedId}</td>
                  <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{h.harvestDate}</td>
                  <td className="px-3 py-3 tabular-nums font-semibold text-foreground">{h.wetWeightKg}</td>
                  <td className="px-3 py-3 tabular-nums text-foreground">{h.dryWeightKg ?? '—'}</td>
                  <td className="px-3 py-3 tabular-nums text-foreground">{h.volumeL ?? '—'}</td>
                  <td className="px-3 py-3">
                    <StatusBadge variant={gradeVariant(h.qualityGrade)} />
                  </td>
                  <td className="px-3 py-3 tabular-nums text-foreground">{h.moisturePct != null ? `${h.moisturePct}%` : '—'}</td>
                  <td className="px-3 py-3 text-muted-foreground">{h.loggedBy || '—'}</td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => setDeleteId(h.id)}
                      className="p-1.5 rounded-lg hover:bg-danger-bg text-muted-foreground hover:text-danger transition-colors"
                      title="Delete this harvest record — this cannot be undone"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-4 py-3 border-t border-border">
        <span className="text-xs text-muted-foreground">{filtered.length} of {harvests.length} records</span>
      </div>

      <ConfirmModal
        open={!!deleteId}
        title="Delete harvest record?"
        message="This harvest batch record will be permanently removed. This cannot be undone."
        confirmLabel="Delete Harvest"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}